import { computed, inject, Injectable, InjectionToken, signal } from '@angular/core';
import {
  AccountInfo,
  AuthenticationResult,
  EventType,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';
import { environment } from '../envs/environment';

export const MSAL_INSTANCE = new InjectionToken<PublicClientApplication>('MSAL_INSTANCE', {
  factory: () => new PublicClientApplication(environment.authConfig),
});

@Injectable({ providedIn: 'root' })
export class Auth {
  readonly #app = inject(MSAL_INSTANCE);
  readonly #user = signal<AccountInfo | null>(null);

  readonly #token = signal<string>('');

  readonly isAuthenticated = computed(() => this.#user() !== null);
  readonly email = computed(() => this.#user()?.username?.toLowerCase() ?? '');
  readonly token = this.#token.asReadonly();

  #redirectState: string | null = null;

  readonly #init = this.#app.initialize().then(async () => {
    this.#app.addEventCallback(event => {
      if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && event.payload) {
        this.#token.set((event.payload as AuthenticationResult).accessToken);
      }
    });

    const result = await this.#app.handleRedirectPromise();
    if (result?.account) {
      this.#app.setActiveAccount(result.account);
      this.#user.set(result.account);
      this.#redirectState = result.state ?? null;
    } else {
      const accounts = this.#app.getAllAccounts();
      if (accounts.length > 0) {
        this.#app.setActiveAccount(accounts[0]);
        this.#user.set(accounts[0]);
      }
    }
  });

  async getAccessToken(): Promise<string> {
    await this.#init;
    const account = this.#app.getActiveAccount();
    if (!account) return '';
    try {
      const result = await this.#app.acquireTokenSilent({
        scopes: environment.apiScopes,
        account,
      });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        await this.#app.acquireTokenRedirect({
          scopes: environment.apiScopes,
          account,
        });
      }
      return '';
    }
  }

  async logout() {
    await this.#init;
    const account = this.#app.getActiveAccount();
    this.#user.set(null);
    await this.#app.clearCache();
    if (account) {
      await this.#app.logoutRedirect();
    }
  }

  async clear() {
    await this.#init;
    this.#user.set(null);
    await this.#app.clearCache();
  }

  async login(returnUrl: string) {
    await this.#init;
    await this.#app.loginRedirect({ scopes: environment.apiScopes, state: returnUrl });
  }

  async waitForInit(): Promise<string | null> {
    await this.#init;
    const state = this.#redirectState;
    this.#redirectState = null;
    return state;
  }
}
